import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkle, MapPin, ArrowDown, Star, Crown,
  ArrowsClockwise, Check, Download, Heart,
  ShieldCheck, Quotes,
} from "@phosphor-icons/react";
import { Header, Footer, PageShell } from "../components/Layout";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";
import api from "../lib/api";
import { applyCurrencyMethodGate, createRazorpayCheckout } from "../lib/razorpayOptions";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// Two-column feature list — reflects what's actually inside the PDF dossier.
const FEATURES_LEFT = [
  "Personalised Detailed Birth Chart",
  "Favourable Gemstones",
  "Wealth Outlook",
  "Planetary Strengths & Dashas",
  "Rudraksha Selection (personalized Mukhi bead)",
  "Sade Sati Live Check & Remedies",
];
const FEATURES_RIGHT = [
  "6 Doshas Screened (Manglik, Pitru, Grahan, Kalsarpa, Putra, Vish)",
  "Numerology (Mulank · Bhagyank · Soul Urge · Lo Shu Grid)",
  "Lucky Points (days, colours, numbers, mobile-last-4, PINs)",
  "Deep Personality Reading (Lagna + Moon + Nakshatra)",
  "20-Year Arc — Career, Relationships & Health scores",
];

// Testimonials — verbatim from astrokuber.com/vedic-astrology-report/
const TESTIMONIALS = [
  { name: "Prashant",  photo: "https://astrokuber.com/wp-content/uploads/2024/10/525336fe-197a-4beb-a809-3e1ce1200963.jpeg",
    text: "I opted for online horoscope consultations as well as their 20 years detailed 55+ pager report and was impressed by the quick, accurate, and insightful information. The team of highly qualified and experienced astrologers is incredibly professional and customer-focused, making me feel at ease every time I reach out. Their dedication to helping clients is truly commendable — highly recommended!" },
  { name: "Saurav",    photo: "https://astrokuber.com/wp-content/uploads/2024/10/IMG_7643.jpeg",
    text: "AstroKuber's yearly reports and consultancy are just OUTSTANDING, offering practical and positive solutions that truly work. After following their recommended changes, I've experienced real, noticeable results. Sachin ji is a top-notch astrologer and Vastu consultant, and his expertise is unmatched. Highly recommended for anyone seeking genuine guidance!" },
  { name: "Madhulika", photo: "https://astrokuber.com/wp-content/uploads/2024/09/IMG_8032.jpg",
    text: "I am beyond thrilled with my consultation with AstroKuber founder, Sachin and grabbing this report! After consulting many astrologers, I finally found someone who provided clear, quick, and effective solutions to all my concerns. His insights are unmatched, and I am confident I'll be turning to him for guidance for the rest of my life! Highly recommended!" },
  { name: "Priya",     photo: "https://astrokuber.com/wp-content/uploads/2024/10/IMG_7642.jpeg",
    text: "I have never came across such a detailed report in Astrology. AstroKuber's astrologers as well as reports provided me clear and insightful guidance on future, helping me understand the right path toward a brighter and more promising future. Highly recommended!" },
  { name: "Mannat",    photo: "https://astrokuber.com/wp-content/uploads/2024/10/c5816449-47f3-470b-a1a0-90ac5262b838.jpeg",
    text: "AstroKuber team truly excels in their field. I am thoroughly satisfied with their accurate and detailed report and insightful guidance. Thank you, AstroKuber, for your exceptional service!" },
  { name: "Mohini",    photo: "https://astrokuber.com/wp-content/uploads/2024/10/IMG_7665.jpeg",
    text: "Thank you so much for such a wonderful and detailed 55+ page report! I feel truly blessed to have come across AstroKuber and consult with their experts. Their guidance has been invaluable, and I am excited and hopeful for the positive changes ahead in my life." },
];

const STATS = [
  { number: "1 Million+", label: "Happy Customers with Positive Reviews" },
  { number: "55+",        label: "Detailed Insightful Pages in the report" },
  { number: "50+",        label: "Types of Reports allowing you to choose the Best one" },
  { number: "20+",        label: "Years of Experience in Astrology" },
  { number: "5 Million+", label: "Views across social media" },
];

const WHEEL_IMG = "https://astrokuber.com/wp-content/uploads/2024/10/Wheel1-2.png";
const FOUNDER_IMG = "https://astrokuber.com/wp-content/uploads/2024/10/image__2_-removebg-preview-1.png";
const MOON_IMG = "https://astrokuber.com/wp-content/uploads/2024/10/Moon.png";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RZP_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function TwentyYearPredictionPage() {
  const { currency, formatPrice } = useCurrency();
  // GST/Taxes added on TOP — 18% for INR (label "GST"), same for USD ("Taxes").
  const REPORT_PRICE_INR = 899;
  const REPORT_TAX_LABEL = currency === "USD" ? "Taxes (18%)" : "GST (18%)";
  const REPORT_TOTAL_INR = Math.round(REPORT_PRICE_INR * 1.18 * 100) / 100;
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [place, setPlace] = useState("");
  const [timezone, setTimezone] = useState("");

  // Autofill from logged-in user's profile — one-shot (shared hook)
  useBirthDetailsPrefill((bd, u) => {
    if (u?.name) setName((prev) => prev || u.name);
    if (bd?.date) setDate((prev) => prev || bd.date);
    if (bd?.time) {
      const [hh, mm] = String(bd.time).split(":");
      setHour((prev) => prev !== "" ? prev : (hh || ""));
      setMinute((prev) => prev !== "" ? prev : (mm || ""));
    }
    if (bd?.place) {
      setPlace((prev) => prev || bd.place);
      setPicked(true);
    }
    if (bd?.timezone) setTimezone((prev) => prev || bd.timezone);
  });

  // Autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(false);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);
  const wrapperRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [reportId, setReportId] = useState(null);
  const isSuperAdmin = !!user?.is_super_admin;

  // If the caller is signed in and admin has already granted them a
  // 20-Year Report (via /super-admin/grant-free-unlock), surface the
  // download block directly instead of showing them the paywall again.
  useEffect(() => {
    if (!user || reportId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/reports/twenty-year/mine");
        if (cancelled) return;
        if (data?.report?.report_id) {
          setReportId(data.report.report_id);
          if (data.report.granted_by_admin) {
            toast.success("Your 20-Year Report is unlocked — thank the AstroKuber team!");
          }
        }
      } catch (_e) {
        // silent — page still shows the paywall path
      }
    })();
    return () => { cancelled = true; };
  }, [user?.user_id, reportId]);

  // Optional phone — pre-fills Razorpay's contact field so the popup is
  // a 1-tap "Pay" for the user. Country code defaults to +91 for INR and
  // +1 for USD, but the user can change it.
  const COUNTRY_CODES = React.useMemo(() => [
    { code: "+91", flag: "🇮🇳", label: "+91 IN" },
    { code: "+1",  flag: "🇺🇸", label: "+1 US" },
    { code: "+44", flag: "🇬🇧", label: "+44 UK" },
    { code: "+61", flag: "🇦🇺", label: "+61 AU" },
    { code: "+971", flag: "🇦🇪", label: "+971 AE" },
    { code: "+65", flag: "🇸🇬", label: "+65 SG" },
    { code: "+966", flag: "🇸🇦", label: "+966 SA" },
    { code: "+49", flag: "🇩🇪", label: "+49 DE" },
    { code: "+33", flag: "🇫🇷", label: "+33 FR" },
    { code: "+81", flag: "🇯🇵", label: "+81 JP" },
  ], []);
  const [phoneCountry, setPhoneCountry] = useState("+91");
  const [phoneNumber, setPhoneNumber] = useState("");
  // Guest email — only used when no user is signed in. We collect this on
  // the form so Razorpay can be pre-filled AND so the backend can auto-
  // email the PDF after the first download (guests have no /reports
  // library, so this is their re-access path).
  const [guestEmail, setGuestEmail] = useState("");
  const isGuest = !user;
  const guestEmailValid =
    !isGuest ||
    (guestEmail.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim()));
  // Auto-flip country default based on currency the visitor picked
  useEffect(() => {
    setPhoneCountry(currency === "USD" ? "+1" : "+91");
  }, [currency]);
  const fullPhone = phoneNumber.trim()
    ? `${phoneCountry}${phoneNumber.replace(/[^\d]/g, "")}`
    : "";

  // Rotating loading messages while the buy / generate-free flow is in flight.
  // The verify endpoint synchronously renders the 50-page PDF and persists it,
  // which can take 4-8 seconds end-to-end. Without these messages the user
  // sees a single "OPENING CHECKOUT…" label and may think the page is hung.
  // We cycle through 8 phases at 1.6s each so even the slowest renders stay
  // entertaining.
  const LOADING_PHASES = React.useMemo(() => [
    "Figuring out your kundali…",
    "Collecting gemstone insights…",
    "Computing your numerology…",
    "Tracing Vimshottari dasha…",
    "Painting D1–D60 charts…",
    "Calculating Ashtakavarga bindus…",
    "Mapping your Sade Sati windows…",
    "Collating 20-year forecast…",
  ], []);
  const [loadingPhaseIdx, setLoadingPhaseIdx] = useState(0);
  useEffect(() => {
    if (!paying) {
      setLoadingPhaseIdx(0);
      return;
    }
    const id = setInterval(() => {
      setLoadingPhaseIdx((i) => (i + 1) % LOADING_PHASES.length);
    }, 1600);
    return () => clearInterval(id);
  }, [paying, LOADING_PHASES.length]);
  const loadingLabel = LOADING_PHASES[loadingPhaseIdx];

  const generateSuperAdmin = async () => {
    if (!formReady) {
      toast.error("Fill in your details first.");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/reports/twenty-year/generate-free", {
        name: name.trim(),
        birth_details: birthDetails,
      });
      setReportId(data.report_id);
      toast.success("Super-admin free generation complete. Your report is ready.");
      setTimeout(() => {
        document.getElementById("download-block")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate the report.");
    } finally { setPaying(false); }
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onPlaceChange = (val) => {
    setPlace(val);
    setPicked(false);
    setTimezone("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setSearching(false);
      setShowSuggestions(trimmed.length > 0);
      return;
    }
    setSearching(true);
    setShowSuggestions(true);
    const myReqId = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/search", { params: { q: trimmed } });
        if (myReqId !== reqIdRef.current) return;
        const arr = Array.isArray(data) ? data : (data?.results || []);
        setSuggestions(arr);
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 220);
  };

  const onPickSuggestion = (s) => {
    setPlace(s.display || s.label || s.name);
    setTimezone(s.timezone || "");
    setPicked(true);
    setShowSuggestions(false);
  };

  const formReady = name.trim() && date && hour !== "" && minute !== "" && place && timezone;
  const birthDetails = formReady ? {
    date,
    time: `${hour}:${minute}`,
    place,
    timezone,
  } : null;

  const onPreview = async () => {
    if (!formReady) {
      toast.error("Fill in your name, date of birth, time of birth and place first.");
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post("/reports/twenty-year/preview", {
        name: name.trim(),
        birth_details: birthDetails,
      });
      setPreview(data);
      setTimeout(() => {
        document.getElementById("preview-block")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the preview.");
    } finally { setPreviewing(false); }
  };

  const onPay = async () => {
    if (!formReady) {
      toast.error("Fill in your details first.");
      return;
    }
    if (isGuest && !guestEmailValid) {
      toast.error("Please enter a valid email — your report will be sent here.");
      return;
    }
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error("Could not load the payment gateway. Try again in a few seconds.");
        setPaying(false);
        return;
      }
      const { data: order } = await api.post("/reports/twenty-year/order", {
        name: name.trim(),
        birth_details: birthDetails,
        display_currency: currency,
        phone: fullPhone || undefined,
        // For guests, persist the email server-side so the backend can
        // (1) pre-fill the Razorpay popup and (2) auto-email the PDF after
        // the first download. Signed-in users skip this — their auth email
        // is used instead.
        guest_email: isGuest ? guestEmail.trim().toLowerCase() : undefined,
      });
      // Trigger conversion event so Google Ads tracking fires
      try {
        if (window.gtag) {
          window.gtag("event", "begin_checkout", {
            currency: order.currency,
            value: order.amount,
            items: [{ item_id: "twenty_year", item_name: "20-Year Vedic Forecast" }],
          });
        }
      } catch { /* ignore */ }
      const rzp = createRazorpayCheckout({
        key: order.key_id,
        amount: order.amount_minor,
        currency: order.currency,
        order_id: order.order_id,
        name: "AstroKuber · 20-Year Forecast",
        description: "Your custom 40+ page Vedic dossier",
        image: "/static/avatars/logo-square.png",
        prefill: {
          // Forward signed-in user's real email so the Razorpay popup shows
          // it instead of the default "void@razorpay.com" placeholder.
          // For guests we forward the email they just typed in our form.
          name: order.prefill?.name || name,
          email:
            order.prefill?.email ||
            user?.email ||
            (isGuest ? guestEmail.trim().toLowerCase() : "") ||
            "",
          contact: order.prefill?.contact || fullPhone || "",
        },
        // When we already KNOW the email + name (signed-in user OR guest
        // who typed their email here), lock those fields so Razorpay only
        // asks the user for their phone number. `readonly` keeps the field
        // visible (so they can verify) but blocks editing — UX is faster
        // AND prevents typos that break refund flows.
        readonly:
          order.prefill?.email || user?.email || (isGuest && guestEmailValid)
            ? { email: true, name: true }
            : {},
        theme: { color: "#3F2D1B" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/reports/twenty-year/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            if (window.gtag) {
              window.gtag("event", "conversion_event_purchase", {
                currency: order.currency,
                value: order.amount,
                transaction_id: resp.razorpay_order_id,
              });
            }
            setReportId(v.report_id);
            toast.success("Payment successful! Your report is ready.");
            setTimeout(() => {
              document.getElementById("download-block")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed; contact support.");
          } finally { setPaying(false); }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
      setPaying(false);
    }
  };

  const [downloading, setDownloading] = useState(false);
  const onDownload = async () => {
    if (!reportId || downloading) return;
    // IMPORTANT — we MUST fetch the PDF as a blob (same-origin URL) and
    // trigger the browser's native download from that blob.
    //
    // Why not just <a href="${BACKEND}/api/reports/.../download" download>?
    // Because the backend lives on a DIFFERENT origin from the frontend
    // (preview: astro-connect-42.preview… ; production: api host vs
    // astrokuberchat.com). The HTML `download` attribute is SILENTLY
    // IGNORED for cross-origin URLs — every modern browser does a
    // top-level navigation instead, which:
    //   1. unmounts the React SPA (looks like "the whole website loads")
    //   2. shows a multi-second blank tab while the PDF streams
    //   3. on any Cloudflare 524 / back-button, kicks the user out of
    //      their session because the SPA re-mounts from scratch and
    //      hits the auth context cold (auto-logout symptom)
    //
    // Blob-fetch fixes all three: the SPA page never navigates, the
    // browser's native "Save / Open" UI fires from a blob: URL (which
    // IS same-origin and DOES honour the download attribute), and the
    // session is preserved end-to-end.
    setDownloading(true);
    const tid = toast.loading("Preparing your PDF…");
    try {
      const res = await api.get(`/reports/twenty-year/${reportId}/download`, {
        responseType: "blob",
        // Cached PDFs serve in 100-200ms; cold-render fallback can take
        // up to ~8s. Keep a generous timeout so a slow first-load never
        // throws and falls through to an error toast.
        timeout: 60000,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AstroKuber_20YearForecast_${reportId}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      const mb = (blob.size / (1024 * 1024)).toFixed(1);
      toast.success(`Downloaded · ${mb} MB`, { id: tid, duration: 4000 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not download PDF.";
      toast.error(msg, { id: tid });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <PageShell>
      {/* ══ Cosmic-blue backdrop — scoped to this page only ══ */}
      <div className="relative min-h-screen bg-gradient-to-b from-[#050917] via-[#0a1440] to-[#050917]" data-testid="cosmic-backdrop">
        {/* Nebula glows */}
        <div
          className="absolute inset-0 pointer-events-none opacity-80"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 22%, rgba(124,139,255,0.18) 0%, transparent 42%), " +
              "radial-gradient(circle at 82% 68%, rgba(212,175,55,0.12) 0%, transparent 50%), " +
              "radial-gradient(circle at 50% 100%, rgba(94,50,180,0.14) 0%, transparent 45%)",
          }}
        ></div>
        {/* Star field — tiny dots */}
        <div
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(1px 1px at 12% 18%, #ffffff 50%, transparent 60%), " +
              "radial-gradient(1px 1px at 28% 44%, #f0e9d2 50%, transparent 60%), " +
              "radial-gradient(1px 1px at 65% 12%, #ffffff 50%, transparent 60%), " +
              "radial-gradient(1.5px 1.5px at 82% 30%, #ffffff 50%, transparent 60%), " +
              "radial-gradient(1px 1px at 45% 78%, #d4c48a 50%, transparent 60%), " +
              "radial-gradient(1px 1px at 88% 82%, #ffffff 50%, transparent 60%), " +
              "radial-gradient(1px 1px at 15% 88%, #ffffff 50%, transparent 60%)",
            backgroundSize: "600px 600px",
          }}
        ></div>

        <div className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-24" data-testid="twenty-year-page">
        {/* ═══ HERO — urgency headline + wheel ═══ */}
        <section className="text-center" data-testid="hero-section">
          <div className="label-eyebrow text-amber-300 tracking-[0.22em]">Premium · One-time payment</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight font-light text-amber-50 max-w-4xl mx-auto">
            Your Personalized <em className="text-amber-300 not-italic">20 Years Vedic Astrology Report</em> is just
            <br className="hidden sm:block" /> FEW CLICKS AWAY… HURRY UP!
          </h1>
          <div className="mt-8 flex justify-center">
            <div className="relative">
              {/* Soft radial glow so the white dial pops against cosmic bg */}
              <div className="absolute inset-0 -m-6 rounded-full bg-gradient-radial from-amber-200/40 via-amber-300/10 to-transparent blur-2xl"
                   style={{ background: "radial-gradient(circle, rgba(255,214,120,0.35) 0%, rgba(255,214,120,0.08) 40%, transparent 70%)" }}></div>
              <img
                src={WHEEL_IMG}
                alt="Vedic astrology wheel"
                loading="lazy"
                className="relative w-32 sm:w-40 lg:w-48 select-none pointer-events-none drop-shadow-[0_0_20px_rgba(255,215,140,0.35)] animate-[spin_60s_linear_infinite]"
              />
            </div>
          </div>
        </section>

        {/* ═══ PRICE BANNER ═══ */}
        <section className="mt-6 text-center" data-testid="price-banner">
          <div className="inline-block px-6 py-3 rounded-full bg-gradient-to-r from-amber-100 via-yellow-100 to-amber-100 border-2 border-amber-400 shadow-[0_4px_24px_rgba(255,214,120,0.35)]">
            <span className="font-serif-display text-xl sm:text-2xl text-[#3F2D1B]">
              Life Kundli with 20 Years Prediction @ <span className="text-[#7C2D12] font-semibold">{formatPrice(REPORT_PRICE_INR)}</span> Only
            </span>
          </div>
          <div className="mt-2 text-[11px] text-amber-200/70">
            + {REPORT_TAX_LABEL} · instant download · free preview below · no signup needed
          </div>
        </section>

        {/* ═══ FORM — ENTER YOUR BIRTH DETAILS HERE ═══ */}
        <section className="mt-14" data-testid="form-section">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-amber-50 text-center font-light uppercase tracking-wide">
            Enter your birth details here
          </h2>
          <p className="text-amber-200/70 text-sm sm:text-base text-center mt-3 max-w-xl mx-auto">
            Please be accurate with your birth details for the most accurate report.
          </p>
          <div className="mt-7 max-w-2xl mx-auto rounded-3xl border border-amber-200 bg-white p-5 sm:p-7 shadow-[0_4px_24px_rgba(63,45,27,0.05)]">
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">Full name (as on birth certificate)</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Patricia Natalia"
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                  data-testid="ty-input-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">Date of birth</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                    data-testid="ty-input-date"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">Time of birth (24h)</label>
                  <div className="mt-1 flex gap-2">
                    <select
                      value={hour}
                      onChange={(e) => setHour(e.target.value)}
                      className="flex-1 px-2 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                      data-testid="ty-input-hour"
                    >
                      <option value="">HH</option>
                      {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <select
                      value={minute}
                      onChange={(e) => setMinute(e.target.value)}
                      className="flex-1 px-2 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                      data-testid="ty-input-minute"
                    >
                      <option value="">MM</option>
                      {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div ref={wrapperRef} className="relative">
                <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">Place of birth</label>
                <div className="mt-1 relative">
                  <MapPin size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input
                    value={place}
                    onChange={(e) => onPlaceChange(e.target.value)}
                    onFocus={() => place.length >= 2 && setShowSuggestions(true)}
                    placeholder="Start typing your birth city..."
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                    data-testid="ty-input-place"
                  />
                </div>
                {showSuggestions && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-amber-200 rounded-lg shadow-xl max-h-60 overflow-auto z-20">
                    {searching && <div className="px-3 py-2 text-xs text-muted">Searching…</div>}
                    {!searching && suggestions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted">
                        {place.length < 2 ? "Type at least 2 characters" : "No matches — try a nearby major city"}
                      </div>
                    )}
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onPickSuggestion(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50"
                        data-testid={`ty-place-suggestion-${i}`}
                      >
                        <div className="font-medium">{s.display || s.label || s.name}</div>
                        <div className="text-[11px] text-muted">{s.timezone || ""}</div>
                      </button>
                    ))}
                  </div>
                )}
                {picked && timezone && (
                  <div className="mt-1 text-[11px] text-emerald-700 inline-flex items-center gap-1">
                    <Check size={10} weight="bold" /> Timezone resolved: {timezone}
                  </div>
                )}
              </div>
            </div>
            {/* Guest email — only shown when the buyer isn't signed in.
                Required for guests so we can pre-fill Razorpay AND auto-
                email the PDF after the first download (guests have no
                /reports library to come back to). Signed-in users skip
                this — their auth email is used instead. */}
            {isGuest && (
              <div className="mt-4">
                <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600 flex items-center gap-2">
                  Email <span className="text-rose-600">*</span>
                  <span className="text-stone-400 font-normal normal-case tracking-normal">
                    — your PDF will be delivered here
                  </span>
                </label>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="you@gmail.com"
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                  data-testid="ty-input-guest-email"
                />
                <div className="mt-1 text-[11px] text-stone-500">
                  We email a copy of your 55+ page PDF the moment you download
                  it — no spam, just your report.
                </div>
              </div>
            )}
            {/* Optional phone — speeds up Razorpay checkout. Country code +
                local number; we pre-fill the country based on the chosen
                currency but the user can override at any time. */}
            <div className="mt-4">
              <label className="text-[11px] font-bold tracking-wider uppercase text-stone-600">
                Phone (optional, for faster checkout)
              </label>
              <div className="mt-1 flex gap-2">
                <select
                  value={phoneCountry}
                  onChange={(e) => setPhoneCountry(e.target.value)}
                  className="w-24 px-2 py-2.5 rounded-lg border border-amber-300 bg-white text-[#3F2D1B] text-sm focus:outline-none focus:border-[#3F2D1B]"
                  data-testid="ty-input-phone-country"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 14))}
                  placeholder="9876543210"
                  className="flex-1 px-3 py-2.5 rounded-lg border border-amber-300 text-[#3F2D1B] focus:outline-none focus:border-[#3F2D1B]"
                  data-testid="ty-input-phone-number"
                />
              </div>
              <div className="mt-1 text-[11px] text-stone-500">
                Pre-fills the Razorpay checkout · only used for payment confirmation.
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                disabled={!formReady || previewing}
                onClick={onPreview}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-[#3F2D1B] text-[#3F2D1B] text-xs font-bold tracking-wider hover:bg-amber-50 disabled:opacity-50"
                data-testid="ty-btn-preview"
              >
                <ArrowsClockwise size={14} weight="bold" /> {previewing ? "BUILDING…" : "FREE PREVIEW"}
              </button>
              {isSuperAdmin ? (
                <button
                  type="button"
                  disabled={!formReady || paying}
                  onClick={generateSuperAdmin}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-600 text-white text-xs font-bold tracking-wider hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="ty-btn-super-admin-generate"
                  title="Super-admin · skip payment, generate the full report instantly"
                >
                  <ShieldCheck size={14} weight="fill" /> {paying ? loadingLabel.toUpperCase() : "SUPER-ADMIN · GENERATE FREE"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!formReady || paying || (isGuest && !guestEmailValid)}
                  onClick={onPay}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#3F2D1B] text-amber-50 text-xs font-bold tracking-wider hover:bg-[#2c1f12] disabled:opacity-50"
                  data-testid="ty-btn-pay"
                >
                  <Crown size={14} weight="fill" /> {paying ? loadingLabel.toUpperCase() : `BUY · ${formatPrice(REPORT_TOTAL_INR)}`}
                </button>
              )}
            </div>
            <div className="text-[10px] text-stone-500 text-center pt-2" data-testid="ty-btn-gst-note">
              {formatPrice(REPORT_PRICE_INR)} + {REPORT_TAX_LABEL} &middot; secure checkout
            </div>
          </div>
        </section>

        {/* Preview block — high-conversion premium reveal */}
        {preview && (
          <section id="preview-block" className="mt-16" data-testid="preview-block">
            {/* Reveal header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-200 via-yellow-200 to-amber-200 text-amber-900 text-[10px] font-bold tracking-[0.18em] uppercase shadow-sm">
                <Sparkle size={11} weight="fill" /> Your chart · revealed
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3F2D1B] mt-3 font-light">
                Meet the soul we just <em className="text-gold not-italic">decoded</em>
              </h2>
              <p className="text-muted text-sm mt-2 max-w-xl mx-auto">
                A 2-minute peek at what&apos;s waiting in your 55+ page dossier. Computed live from your birth details — every number below is Swiss-Ephemeris precise.
              </p>
            </div>

            {/* Dark royal card */}
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(63,45,27,0.5)]">
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#1f1408] via-[#3F2D1B] to-[#1f1408]"></div>
              <div
                className="absolute inset-0 opacity-30 mix-blend-soft-light pointer-events-none"
                style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #d4af37 0%, transparent 40%), radial-gradient(circle at 80% 70%, #7C2D12 0%, transparent 50%)" }}
              ></div>
              {/* Gold border */}
              <div className="absolute inset-0 rounded-3xl border border-amber-400/30 pointer-events-none"></div>

              {/* Content */}
              <div className="relative z-10 p-6 sm:p-10 text-amber-50">
                <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                  {/* Left — chart image */}
                  <div className="flex justify-center">
                    {preview.chart_d1_png ? (
                      <div className="relative" data-testid="preview-d1-chart">
                        <div className="absolute -inset-3 bg-amber-400/20 rounded-2xl blur-xl"></div>
                        <img
                          src={preview.chart_d1_png}
                          alt={`${preview.name}'s D1 Rashi Chart`}
                          className="relative w-full max-w-[360px] rounded-xl ring-2 ring-amber-400/50 shadow-2xl"
                        />
                        <div className="text-center mt-2 text-[11px] text-amber-200/80 italic">
                          Your D1 Rashi Chart · one of 16 in the full report
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {/* Right — signature */}
                  <div className="lg:max-w-sm space-y-5">
                    <div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-amber-400/80">Ascendant rising</div>
                      <div className="font-serif-display text-4xl text-amber-50 mt-1">
                        {preview.lagna?.rashi}
                      </div>
                      <div className="text-amber-200/70 text-xs mt-1">Lord: {preview.lagna?.rashi_lord}</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-amber-400/80">Moon · emotional core</div>
                      <div className="font-serif-display text-2xl text-amber-50 mt-1">
                        {preview.moon?.nakshatra}
                        {preview.moon?.pada ? <span className="text-amber-300/70 text-sm ml-2">pada {preview.moon.pada}</span> : null}
                      </div>
                      <div className="text-amber-200/70 text-xs mt-1">
                        {preview.moon?.rashi} · {preview.moon?.yoni} yoni · {preview.moon?.gana} gana
                      </div>
                      <div className="text-amber-300/60 text-[11px] mt-0.5">Nakshatra deity: <em className="text-amber-200 not-italic">{preview.moon?.deity}</em></div>
                    </div>
                    {preview.teaser_strength && (
                      <div className="rounded-xl bg-gradient-to-br from-amber-400/15 to-amber-500/5 border border-amber-400/30 p-4">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-amber-300">Your soul-strength</div>
                        <div className="text-amber-50 text-sm mt-1 leading-relaxed font-serif-display italic">
                          &ldquo;{preview.teaser_strength}.&rdquo;
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sections grid — 5 columns of premium chips */}
                <div className="mt-10 pt-8 border-t border-amber-400/20">
                  <div className="text-center text-[10px] tracking-[0.22em] uppercase text-amber-300/80 mb-4">
                    Everything that unlocks at {formatPrice(REPORT_PRICE_INR)} + {REPORT_TAX_LABEL}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="preview-section-grid">
                    {preview.sections.map((s, i) => (
                      <div
                        key={i}
                        className="rounded-lg bg-amber-50/5 border border-amber-400/15 backdrop-blur-sm px-3 py-2.5 text-[11px] text-amber-100 leading-snug"
                      >
                        <Check size={10} weight="bold" className="text-amber-400 inline-block mr-1 mb-0.5" />
                        {s}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Trust strip */}
                <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center" data-testid="preview-trust-strip">
                  <div className="rounded-lg bg-amber-50/5 border border-amber-400/15 py-3">
                    <div className="font-serif-display text-2xl text-amber-200">⭐ 4.9</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mt-0.5">from 12,400+ seekers</div>
                  </div>
                  <div className="rounded-lg bg-amber-50/5 border border-amber-400/15 py-3">
                    <div className="font-serif-display text-2xl text-amber-200">100%</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mt-0.5">Swiss-Ephemeris accurate</div>
                  </div>
                  <div className="rounded-lg bg-amber-50/5 border border-amber-400/15 py-3">
                    <div className="font-serif-display text-2xl text-amber-200">40+</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mt-0.5">pages · 16 charts inside</div>
                  </div>
                  <div className="rounded-lg bg-amber-50/5 border border-amber-400/15 py-3">
                    <div className="font-serif-display text-2xl text-amber-200">{"<"}2 min</div>
                    <div className="text-[10px] uppercase tracking-wider text-amber-300/70 mt-0.5">instant download</div>
                  </div>
                </div>

                {/* Value-anchor */}
                <div className="mt-8 rounded-2xl bg-gradient-to-r from-amber-400/10 via-amber-300/15 to-amber-400/10 border border-amber-400/30 p-4 text-center" data-testid="preview-value-anchor">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/90">Compare</div>
                  <div className="text-amber-100 text-sm sm:text-base mt-1">
                    A single astrologer consultation: <span className="line-through opacity-60">₹3,500 – ₹8,000</span>{" "}
                    · An AstroSage detailed report: <span className="line-through opacity-60">₹1,499 – ₹2,999</span>
                  </div>
                  <div className="text-amber-200 mt-2 font-serif-display text-xl">
                    Your 20-Year Vedic Forecast: <span className="text-amber-100 font-bold">{formatPrice(REPORT_PRICE_INR)}</span> + {REPORT_TAX_LABEL} · one-time
                  </div>
                </div>

                {/* Big shimmer CTA */}
                <div className="mt-7 flex flex-col items-center">
                  <button
                    type="button"
                    disabled={paying}
                    onClick={isSuperAdmin ? generateSuperAdmin : onPay}
                    className={`group relative w-full max-w-md inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full text-sm font-bold tracking-[0.12em] uppercase shadow-[0_8px_24px_-4px_rgba(212,175,55,0.5)] transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100 overflow-hidden ${isSuperAdmin ? "bg-gradient-to-r from-emerald-500 via-emerald-400 to-emerald-500 text-white" : "bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 text-[#3F2D1B]"}`}
                    data-testid="ty-btn-pay-preview"
                  >
                    {/* Shimmer */}
                    {!isSuperAdmin && (
                      <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 pointer-events-none"></span>
                    )}
                    {isSuperAdmin ? (
                      <>
                        <ShieldCheck size={18} weight="fill" />
                        {paying ? loadingLabel : "Super-admin · Generate free"}
                      </>
                    ) : (
                      <>
                        <Crown size={18} weight="fill" />
                        {paying ? loadingLabel : `Unlock my report · ${formatPrice(REPORT_TOTAL_INR)}`}
                        <ArrowDown size={14} weight="bold" className="rotate-[-90deg]" />
                      </>
                    )}
                  </button>
                  {!isSuperAdmin && (
                    <div className="mt-3 flex items-center gap-4 text-[11px] text-amber-300/80" data-testid="preview-payment-trust">
                      <span className="inline-flex items-center gap-1"><ShieldCheck size={11} weight="fill" /> Secure Razorpay checkout</span>
                      <span className="hidden sm:inline opacity-60">·</span>
                      <span>UPI · Cards · PayPal</span>
                      <span className="hidden sm:inline opacity-60">·</span>
                      <span>Instant download</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Download block */}
        {reportId && (
          <section id="download-block" className="mt-14" data-testid="download-block">
            <div className="rounded-3xl border-2 border-emerald-400 bg-emerald-50/70 p-8 text-center">
              <Check size={36} weight="fill" className="mx-auto text-emerald-600" />
              <h2 className="font-serif-display text-3xl text-[#3F2D1B] mt-4">Your report is ready</h2>
              <p className="text-muted text-sm mt-1">Click the button below — your browser&apos;s native download will start instantly.</p>
              <button
                type="button"
                onClick={onDownload}
                disabled={downloading}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white text-sm font-bold tracking-wider hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="ty-btn-download"
              >
                {downloading ? (
                  <>
                    <ArrowsClockwise size={16} weight="bold" className="animate-spin" /> PREPARING…
                  </>
                ) : (
                  <>
                    <Download size={16} weight="bold" /> DOWNLOAD PDF
                  </>
                )}
              </button>
              <div className="mt-3 text-[11px] text-stone-500 font-mono break-all">
                Report ID: {reportId}
              </div>
            </div>
          </section>
        )}

        {/* ═══ TRUST INTRO ═══ */}
        <section className="mt-20 text-center" data-testid="trust-intro">
          <div className="flex justify-center mb-6">
            <img
              src={FOUNDER_IMG}
              alt="AstroKuber founder"
              loading="lazy"
              className="w-40 sm:w-52 select-none pointer-events-none"
            />
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-amber-50 font-light max-w-3xl mx-auto leading-tight">
            AstroKuber is India&apos;s Most Trusted Astrology Portal
            <br className="hidden sm:block" />
            <span className="text-amber-300">Serving Clients for More than a Decade</span>
          </h2>
        </section>

        {/* ═══ DISCOVER + FEATURES (two-column list) ═══ */}
        <section className="mt-16" data-testid="discover-section">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-amber-50 text-center font-light max-w-3xl mx-auto leading-tight">
            Discover what your AstroKuber Kundali offers!
          </h2>
          <p className="mt-4 max-w-3xl mx-auto text-center text-amber-200/80 text-sm sm:text-base leading-relaxed">
            Unlock personalized insights, powerful predictions, and expert guidance
            tailored to your life&apos;s journey for the next <strong className="text-amber-100">20 years</strong>.
            AstroKuber is a trusted leader in Vedic Astrology, known for its expertise
            and popularity. As a recognized institution, we provide top-quality services
            in astrology and Vedic sciences, empowering you with the insights and guidance
            you need to thrive. Experience the difference with AstroKuber, where tradition
            meets excellence.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-3 max-w-3xl mx-auto" data-testid="feature-list">
            <ul className="space-y-3">
              {FEATURES_LEFT.map((f, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`feature-left-${i}`}>
                  <div className="mt-1 shrink-0 w-5 h-5 rounded-full bg-amber-400/20 border border-amber-400 flex items-center justify-center">
                    <Check size={12} weight="bold" className="text-amber-300" />
                  </div>
                  <span className="text-amber-50 text-sm sm:text-base">{f}</span>
                </li>
              ))}
            </ul>
            <ul className="space-y-3">
              {FEATURES_RIGHT.map((f, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`feature-right-${i}`}>
                  <div className="mt-1 shrink-0 w-5 h-5 rounded-full bg-amber-400/20 border border-amber-400 flex items-center justify-center">
                    <Check size={12} weight="bold" className="text-amber-300" />
                  </div>
                  <span className="text-amber-50 text-sm sm:text-base">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Repeat CTA to send users back to the form */}
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth", block: "start" })
                || document.querySelector('[data-testid="form-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3F2D1B] text-amber-50 text-xs sm:text-sm font-bold tracking-[0.14em] uppercase hover:bg-[#2c1f12] shadow-[0_6px_20px_-6px_rgba(63,45,27,0.5)] transition"
              data-testid="cta-scroll-to-form"
            >
              <Crown size={16} weight="fill" />
              Click here to get your 20 Years Report Now!
              <ArrowDown size={14} weight="bold" className="rotate-[-90deg]" />
            </button>
          </div>
        </section>

        {/* ═══ TESTIMONIALS ═══ */}
        <section className="mt-20" data-testid="testimonials-section">
          <div className="text-center">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-amber-50 font-light">
              Customer Feedback &amp; Reviews
            </h2>
            <p className="text-amber-200/70 mt-2 italic text-sm sm:text-base">
              Our happy clients have some words to say...
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="relative rounded-2xl bg-white/95 backdrop-blur-sm border border-amber-300/60 p-5 shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
                data-testid={`testimonial-${i}`}
              >
                <Quotes size={28} weight="fill" className="absolute -top-3 left-4 text-amber-400" />
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={t.photo}
                    alt={t.name}
                    loading="lazy"
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-400"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="font-serif-display text-lg text-[#3F2D1B]">{t.name}</div>
                </div>
                <div className="flex text-amber-500 mb-2">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={12} weight="fill" />)}
                </div>
                <p className="text-[13px] text-stone-700 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═══ STATS (moon icons) ═══ */}
        <section className="mt-20" data-testid="stats-section">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 max-w-5xl mx-auto">
            {STATS.map((s, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <img
                  src={MOON_IMG}
                  alt=""
                  loading="lazy"
                  className="w-14 sm:w-16 mx-auto opacity-90 select-none pointer-events-none drop-shadow-[0_0_12px_rgba(255,215,140,0.4)]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="font-serif-display text-2xl sm:text-3xl text-amber-50 mt-2">{s.number}</div>
                <div className="text-[11px] sm:text-xs text-amber-200/70 mt-1 leading-snug px-2">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust strip */}
        <section className="mt-16 text-center text-xs text-amber-200/60 max-w-2xl mx-auto">
          One-time payment · Instant generation · 100% Swiss-Ephemeris accurate ·
          Built on the same engine AstroKuber astrologers use during live readings.
        </section>
        </div>
      </div>
    </PageShell>
  );
}
